import { useEffect, useRef } from 'react';
import { api, type HuntParticipantStatus, type HuntStatusInput } from '../api';
import { stepsToday } from '../hunterHealth';
import { nativeBattery } from '../nativeBattery';

// ─────────────────────────────────────────────────────────────────
// KA1: min status i jakten (batteri, dagens steg, puls, hastighet, höjd,
// högtalare) skickas till servern var 30:e sekund, och visas för de andra
// när de trycker på mig på kartan (ParticipantStatusCard).
//
// Källor i webbvyn: hastighet och höjd från GPS:en, batteri från
// navigator.getBattery (Chrome/Android) eller NativeBattery (iOS-appen, KA1b), steg från
// Hälsa via HunterHealth (bara iOS-appen), puls från klockans pass (watchBridge).
// ─────────────────────────────────────────────────────────────────

export const STATUS_REPORT_MS = 30_000;
const STEPS_REFRESH_MS = 5 * 60_000;
const BATTERY_REFRESH_MS = 5 * 60_000;

/** Bygger det som skickas; värden som saknas skickas inte alls. */
export function buildStatusInput(s: {
  identity?: string | null; battery?: { level: number; charging: boolean } | null; steps?: number | null;
  heartRate?: number | null; speed?: number | null; altitude?: number | null; speakerOn?: boolean | null;
}): HuntStatusInput {
  const out: HuntStatusInput = {};
  if (s.identity) out.identity = s.identity;
  if (s.battery && Number.isFinite(s.battery.level)) { out.battery = s.battery.level; out.charging = s.battery.charging; }
  if (s.steps != null) out.steps = Math.round(s.steps);
  if (s.heartRate != null) out.heartRate = Math.round(s.heartRate);
  // GPS:en ger null (eller NaN) när värdet saknas; negativ hastighet = okänd.
  if (s.speed != null && Number.isFinite(s.speed) && s.speed >= 0) out.speed = s.speed;
  if (s.altitude != null && Number.isFinite(s.altitude)) out.altitude = s.altitude;
  if (s.speakerOn != null) out.speakerOn = s.speakerOn;
  return out;
}

type BatteryManagerLike = { level: number; charging: boolean };

/** Skickar min status medan jakten pågår. getIdentity = min LiveKit-identitet (kopplar mig till min markör). */
export function useHuntStatusReporter(opts: {
  meetingId: string | null; enabled: boolean; getIdentity: () => string | null; speakerOn: boolean; heartRate: number | null;
}) {
  const { meetingId, enabled } = opts;
  const live = useRef(opts);
  live.current = opts;
  const fix = useRef<{ speed: number | null; altitude: number | null }>({ speed: null, altitude: null });
  const battery = useRef<BatteryManagerLike | null>(null);
  const steps = useRef<number | null>(null);
  const send = useRef<() => void>(() => {});

  useEffect(() => {
    if (!meetingId || !enabled) return;
    let alive = true;
    send.current = () => {
      if (!alive) return;
      const o = live.current;
      const input = buildStatusInput({
        identity: o.getIdentity(), battery: battery.current, steps: steps.current, heartRate: o.heartRate,
        speed: fix.current.speed, altitude: fix.current.altitude, speakerOn: o.speakerOn,
      });
      api.reportHuntStatus(meetingId, input).catch(() => { /* äldre API eller tillfälligt nätfel */ });
    };
    // Egen GPS-bevakning för hastighet och höjd (kartans bevakning sparar bara positionen).
    let watchId: number | null = null;
    if (navigator.geolocation) {
      watchId = navigator.geolocation.watchPosition(
        p => { fix.current = { speed: p.coords.speed, altitude: p.coords.altitude }; },
        () => {},
        { enableHighAccuracy: true, maximumAge: 10_000, timeout: 30_000 },
      );
    }
    const nav = navigator as Navigator & { getBattery?: () => Promise<BatteryManagerLike> };
    nav.getBattery?.().then(b => { if (alive) battery.current = b; }).catch(() => {});
    // iOS-appen: egen avläsning (WKWebView saknar getBattery), uppdateras var 5:e minut.
    const loadBattery = () => { void nativeBattery().then(b => { if (alive && b) battery.current = b; }); };
    loadBattery();
    const batteryTid = setInterval(loadBattery, BATTERY_REFRESH_MS);
    const loadSteps = () => { void stepsToday().then(n => { steps.current = n; }); };
    loadSteps();
    const stepsTid = setInterval(loadSteps, STEPS_REFRESH_MS);
    const first = setTimeout(() => send.current(), 3000);
    const tid = setInterval(() => send.current(), STATUS_REPORT_MS);
    return () => {
      alive = false;
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      clearInterval(stepsTid); clearInterval(batteryTid); clearInterval(tid); clearTimeout(first);
      send.current = () => {};
    };
  }, [meetingId, enabled]);

  // Högtalaren syns direkt hos de andra.
  const firstSpeaker = useRef(true);
  useEffect(() => {
    if (firstSpeaker.current) { firstSpeaker.current = false; return; }
    send.current();
  }, [opts.speakerOn]);
}

/** Markörens nyckel på kartan: LiveKit-identitet, eller "watch:<e-post>" för den som bara har klockan med. */
export function findStatus(rows: HuntParticipantStatus[], key: string): HuntParticipantStatus | null {
  if (key.startsWith('watch:')) {
    const email = key.slice(6).toLowerCase();
    return rows.find(r => r.userEmail === email) ?? null;
  }
  return rows.find(r => r.identity === key) ?? null;
}

const nf = (n: number, d = 0) => n.toLocaleString('sv-SE', { minimumFractionDigits: d, maximumFractionDigits: d });

export const formatBattery = (b: number | null, charging?: boolean | null) =>
  (b == null ? null : `${Math.round(b * 100)} %${charging ? ' · laddar' : ''}`);
export const formatSteps = (n: number | null) => (n == null ? null : nf(n));
export const formatHeartRate = (n: number | null) => (n == null ? null : `${n} slag/min`);
/** m/s → km/h; under 0,5 km/h räknas som stilla. */
export const formatSpeed = (ms: number | null) => {
  if (ms == null) return null;
  const kmh = ms * 3.6;
  return kmh < 0.5 ? 'Står still' : `${nf(kmh, kmh < 10 ? 1 : 0)} km/h`;
};
export const formatAltitude = (m: number | null) => (m == null ? null : `${nf(Math.round(m))} m ö.h.`);
export const formatSpeaker = (on: boolean | null) => (on == null ? null : on ? 'På' : 'Av');

/** "nyss", "för 3 min sedan" */
export function formatAge(updatedAt: number, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - updatedAt) / 1000));
  if (s < 60) return 'nyss';
  return `för ${Math.round(s / 60)} min sedan`;
}
